/**
 * Shared Storage Service
 * Following Feature-Based Modular Architecture
 */

import { getActiveImpersonationToken } from '@/shared/services/impersonation-session'
import { STORAGE_KEYS } from '@/shared/utils/constants'
import { logger } from '@/shared/utils/logger'

export interface StorageItem<T = unknown> {
  value: T
  timestamp: number
  expiresAt?: number
}

class StorageService {
  private prefix: string

  constructor(prefix = 'afcripto_') {
    this.prefix = prefix
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`
  }

  // Local Storage methods
  setLocal<T>(key: string, value: T, expirationMinutes?: number): void {
    try {
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ...(expirationMinutes ? { expiresAt: Date.now() + expirationMinutes * 60 * 1000 } : {}),
      }

      localStorage.setItem(this.getKey(key), JSON.stringify(item))
    } catch (error) {
      logger.error('Error saving to localStorage:', error)
    }
  }

  getLocal<T>(key: string, defaultValue?: T): T | null {
    try {
      const stored = localStorage.getItem(this.getKey(key))
      if (!stored) return defaultValue ?? null

      const item: StorageItem<T> = JSON.parse(stored)

      // Client does not validate expiration - server is the single source of truth
      // If token is expired, server will respond with 401 and trigger auto-refresh
      // This eliminates race conditions and keeps validation centralized
      return item.value
    } catch (error) {
      logger.error('Error reading from localStorage:', error)
      return defaultValue ?? null
    }
  }

  removeLocal(key: string): void {
    try {
      localStorage.removeItem(this.getKey(key))
    } catch (error) {
      logger.error('Error removing from localStorage:', error)
    }
  }

  clearLocal(): void {
    try {
      const keys = Object.keys(localStorage)
      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key)
        }
      })
    } catch (error) {
      logger.error('Error clearing localStorage:', error)
    }
  }

  // Session Storage methods
  setSession<T>(key: string, value: T): void {
    try {
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
      }

      sessionStorage.setItem(this.getKey(key), JSON.stringify(item))
    } catch (error) {
      logger.error('Error saving to sessionStorage:', error)
    }
  }

  getSession<T>(key: string, defaultValue?: T): T | null {
    try {
      const stored = sessionStorage.getItem(this.getKey(key))
      if (!stored) return defaultValue ?? null

      const item: StorageItem<T> = JSON.parse(stored)
      return item.value
    } catch (error) {
      logger.error('Error reading from sessionStorage:', error)
      return defaultValue ?? null
    }
  }

  removeSession(key: string): void {
    try {
      sessionStorage.removeItem(this.getKey(key))
    } catch (error) {
      logger.error('Error removing from sessionStorage:', error)
    }
  }

  clearSession(): void {
    try {
      const keys = Object.keys(sessionStorage)
      keys.forEach((key) => {
        if (key.startsWith(this.prefix)) {
          sessionStorage.removeItem(key)
        }
      })
    } catch (error) {
      logger.error('Error clearing sessionStorage:', error)
    }
  }

  // Cookie methods (for simple values)
  setCookie(name: string, value: string, days?: number): void {
    try {
      let expires = ''
      if (days) {
        const date = new Date()
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
        expires = `; expires=${date.toUTCString()}`
      }
      document.cookie = `${this.getKey(name)}=${value}${expires}; path=/`
    } catch (error) {
      logger.error('Error setting cookie:', error)
    }
  }

  getCookie(name: string): string | null {
    try {
      const nameEQ = `${this.getKey(name)}=`
      const ca = document.cookie.split(';')

      for (let i = 0; i < ca.length; i++) {
        let c = ca[i]
        if (!c) continue
        while (c.startsWith(' ')) c = c.substring(1, c.length)
        if (c.startsWith(nameEQ)) return c.substring(nameEQ.length, c.length)
      }

      return null
    } catch (error) {
      logger.error('Error reading cookie:', error)
      return null
    }
  }

  removeCookie(name: string): void {
    this.setCookie(name, '', -1)
  }

  // Convenience methods for common application data
  setAuthToken(token: string): void {
    // Store token WITHOUT client-side expiration
    // Server is the single source of truth for token validation
    // If token is expired, server will respond with 401 and trigger auto-refresh
    this.setLocal(STORAGE_KEYS.AUTH_TOKEN, token)
  }

  /**
   * The bearer that outgoing requests MUST carry.
   *
   * While a super-admin impersonates someone, that is the IMPERSONATION token,
   * not their own. This is the only choke point every caller shares — the
   * api.service header fallback, the ~30 hand-rolled `Authorization` headers on
   * download/SSE/WebSocket paths, and `authService.initializeAuth()`. Resolving
   * it anywhere else is how the impersonated token got minted, stored, and then
   * never sent: `initializeAuth` re-read the admin's own token from localStorage
   * and overwrote the header the impersonation context had just set (issue
   * #2145 — the app painted "viewing as Elena" while showing the admin's own
   * 12 import sources).
   *
   * Use `getOwnAuthToken()` when you specifically need the admin's own
   * credentials (returning from an impersonation).
   */
  getAuthToken(): string | null {
    return getActiveImpersonationToken() ?? this.getLocal(STORAGE_KEYS.AUTH_TOKEN)
  }

  /**
   * The signed-in account's own token, ignoring any impersonation in flight.
   * This is what the admin returns to when the session ends or expires.
   */
  getOwnAuthToken(): string | null {
    return this.getLocal(STORAGE_KEYS.AUTH_TOKEN)
  }

  removeAuthToken(): void {
    this.removeLocal(STORAGE_KEYS.AUTH_TOKEN)
  }

  setUserPreferences(preferences: Record<string, unknown>): void {
    this.setLocal(STORAGE_KEYS.USER_PREFERENCES, preferences)
  }

  getUserPreferences(): Record<string, unknown> | null {
    return this.getLocal(STORAGE_KEYS.USER_PREFERENCES)
  }

  setTheme(theme: string): void {
    this.setLocal(STORAGE_KEYS.THEME, theme)
  }

  getTheme(): string | null {
    return this.getLocal(STORAGE_KEYS.THEME)
  }

  setLastRoute(route: string): void {
    this.setSession(STORAGE_KEYS.LAST_ROUTE, route)
  }

  getLastRoute(): string | null {
    return this.getSession(STORAGE_KEYS.LAST_ROUTE)
  }

  setTableSettings(tableId: string, settings: Record<string, unknown>): void {
    const allSettings: Record<string, unknown> =
      this.getLocal(STORAGE_KEYS.TABLE_SETTINGS, {}) ?? {}
    allSettings[tableId] = settings
    this.setLocal(STORAGE_KEYS.TABLE_SETTINGS, allSettings)
  }

  getTableSettings(tableId: string): Record<string, unknown> | null {
    const allSettings: Record<string, unknown> =
      this.getLocal(STORAGE_KEYS.TABLE_SETTINGS, {}) ?? {}
    return (allSettings[tableId] as Record<string, unknown>) || null
  }

  // Utility methods
  getStorageSize(): { localStorage: number; sessionStorage: number } {
    let localSize = 0
    let sessionSize = 0

    try {
      // Calculate localStorage size
      for (const key in localStorage) {
        if (
          Object.prototype.hasOwnProperty.call(localStorage, key) &&
          key.startsWith(this.prefix)
        ) {
          localSize += localStorage[key].length + key.length
        }
      }

      // Calculate sessionStorage size
      for (const key in sessionStorage) {
        if (
          Object.prototype.hasOwnProperty.call(sessionStorage, key) &&
          key.startsWith(this.prefix)
        ) {
          sessionSize += sessionStorage[key].length + key.length
        }
      }
    } catch (error) {
      logger.error('Error calculating storage size:', error)
    }

    return { localStorage: localSize, sessionStorage: sessionSize }
  }

  isStorageAvailable(): { localStorage: boolean; sessionStorage: boolean } {
    let localAvailable = false
    let sessionAvailable = false

    try {
      const testKey = '__storage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      localAvailable = true
    } catch (_error) {
      localAvailable = false
    }

    try {
      const testKey = '__storage_test__'
      sessionStorage.setItem(testKey, 'test')
      sessionStorage.removeItem(testKey)
      sessionAvailable = true
    } catch (_error) {
      sessionAvailable = false
    }

    return { localStorage: localAvailable, sessionStorage: sessionAvailable }
  }
}

// Create singleton instance
export const storageService = new StorageService()
